import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePermission } from '@/hooks/useAuth'
import { salesLeadKeys } from '@/modules/sales/hooks/useSalesLeads'
import { callService, type StartCallInput, type WrapUpInput } from '@/modules/calls/services/call.service'

export const callKeys = {
  all: ['calls'] as const,
  config: ['calls', 'config'] as const,
  detail: (id: string) => ['calls', 'detail', id] as const,
}

/** Dialer config + my unfinished calls. Only fetched for users with Make calls. */
export function useCallConfig() {
  const canDial = usePermission('calls.dial')
  return useQuery({
    queryKey: callKeys.config,
    queryFn: callService.config,
    enabled: canDial,
    staleTime: 30_000,
  })
}

/**
 * Tracked calling is on for this user: Make calls AND the server's master
 * switch. False while loading — the plain phone link is the safe default.
 */
export function useCallsEnabled(): boolean {
  const { data } = useCallConfig()
  return Boolean(data?.canDial)
}

export function useCallDetail(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: callKeys.detail(id ?? ''),
    queryFn: () => callService.get(id as string),
    enabled: Boolean(id) && enabled,
  })
}

export function useStartCall() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: StartCallInput) => callService.start(input),
    onSettled: () => void qc.invalidateQueries({ queryKey: callKeys.config }),
  })
}

export function useCancelCall() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => callService.cancel(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: callKeys.config }),
  })
}

export function useWrapUpCall() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: WrapUpInput }) => callService.wrapUp(id, input),
    onSuccess: ({ call }) => {
      void qc.invalidateQueries({ queryKey: callKeys.all })
      // Lead status / callback / contact history changed server-side.
      void qc.invalidateQueries({ queryKey: salesLeadKeys.all })
      if (call.salesLeadId)
        void qc.invalidateQueries({
          queryKey: salesLeadKeys.detail(call.salesLeadId),
        })
    },
  })
}
